import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Search, Play, Pause, Copy, Trash2, Zap, Mail, Bell, ClipboardList, ChevronRight, MoreHorizontal, Sparkles, Download, Clock } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_TABS = ['All', 'Active', 'Draft', 'Paused', 'Templates'];

const typeColors = {
  'Listing Agent': 'bg-blue-100 text-blue-700 border-blue-200',
  'Buyer': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'General': 'bg-gray-100 text-gray-700 border-gray-200',
  'Custom': 'bg-purple-100 text-purple-700 border-purple-200',
};

const statusColors = {
  active: 'bg-emerald-500',
  draft: 'bg-gray-400',
  paused: 'bg-amber-500',
};

const statusLabels = {
  active: 'Active',
  draft: 'Draft',
  paused: 'Paused',
};

function StepIcon({ type }) {
  if (type === 'send_email') return <Mail className="h-3.5 w-3.5" />;
  if (type === 'create_task') return <ClipboardList className="h-3.5 w-3.5" />;
  return <Bell className="h-3.5 w-3.5" />;
}

function formatDelay(step) {
  if (step.delay_type === 'immediate') return 'Immediately';
  if (step.delay_value === 1) return `After 1 ${step.delay_type === 'hours' ? 'hour' : 'day'}`;
  return `After ${step.delay_value} ${step.delay_type}`;
}

export default function DripCampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [actionMenuId, setActionMenuId] = useState(null);
  const [prebuiltTemplates, setPrebuiltTemplates] = useState([]);
  const [installing, setInstalling] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    try {
      const params = activeTab !== 'All' && activeTab !== 'Templates' ? { status: activeTab.toLowerCase() } : {};
      const res = await API.get('/drip-campaigns', { params });
      setCampaigns(res.data);
    } catch { toast.error('Failed to load campaigns'); } finally { setLoading(false); }
  }, [activeTab]);

  const fetchPrebuilt = useCallback(async () => {
    try {
      const res = await API.get('/drip-campaigns/prebuilt');
      setPrebuiltTemplates(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (activeTab === 'Templates') { fetchPrebuilt(); }
    else { fetchCampaigns(); }
  }, [activeTab, fetchCampaigns, fetchPrebuilt]);

  const filtered = campaigns.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.campaign_type.toLowerCase().includes(search.toLowerCase())
  );

  const counts = {
    All: campaigns.length,
    Active: campaigns.filter(c => c.status === 'active').length,
    Draft: campaigns.filter(c => c.status === 'draft').length,
    Paused: campaigns.filter(c => c.status === 'paused').length,
  };

  const handleStatusChange = async (campaign, newStatus) => {
    try {
      await API.put(`/drip-campaigns/${campaign.id}`, { status: newStatus });
      toast.success(`Campaign ${newStatus === 'active' ? 'activated' : 'paused'}`);
      fetchCampaigns();
    } catch { toast.error('Failed to update campaign'); }
    setActionMenuId(null);
  };

  const handleDuplicate = async (campaignId) => {
    try {
      await API.post(`/drip-campaigns/${campaignId}/duplicate`);
      toast.success('Campaign duplicated');
      fetchCampaigns();
    } catch { toast.error('Failed to duplicate'); }
    setActionMenuId(null);
  };

  const handleDelete = async (campaignId) => {
    try {
      await API.delete(`/drip-campaigns/${campaignId}`);
      toast.success('Campaign deleted');
      setShowDeleteConfirm(null);
      fetchCampaigns();
    } catch { toast.error('Failed to delete'); }
  };

  const handleInstallPrebuilt = async (templateKey) => {
    setInstalling(templateKey);
    try {
      const res = await API.post(`/drip-campaigns/prebuilt/${templateKey}/install`);
      toast.success(`"${res.data.name}" installed as draft — customize and activate it!`);
      fetchPrebuilt();
      setActiveTab('Draft');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to install');
    } finally { setInstalling(null); }
  };

  return (
    <div data-testid="drip-campaigns-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Drip Campaigns</h1>
          <p className="text-sm text-gray-500 mt-1">Automated follow-up sequences for leads and buyers</p>
        </div>
        <Button
          data-testid="create-campaign-btn"
          onClick={() => navigate('/drip-campaigns/new')}
          className="bg-[#0073ea] hover:bg-[#0060c2] text-white"
        >
          <Plus className="h-4 w-4 mr-2" /> New Campaign
        </Button>
      </div>

      {/* Tabs + Search */}
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_TABS.map(tab => (
          <button
            key={tab}
            data-testid={`tab-${tab.toLowerCase()}`}
            onClick={() => setActiveTab(tab)}
            className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab
                ? 'bg-[#cce5ff] text-[#0073ea] border border-[#0073ea]/30'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100 border border-transparent'
            }`}
          >
            {tab}{tab !== 'Templates' ? ` (${counts[tab]})` : ''}
          </button>
        ))}
        <div className="hidden sm:block flex-1" />
        {activeTab !== 'Templates' && (
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              data-testid="campaign-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search campaigns..."
              className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 pl-9"
            />
          </div>
        )}
      </div>

      {/* Pre-built Templates */}
      {activeTab === 'Templates' && (
        <div className="space-y-4" data-testid="prebuilt-templates-section">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Ready-to-Use Campaign Templates</h2>
            <p className="text-sm text-gray-500 mt-0.5">Install a pre-built campaign in one click. Customize it, then activate.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {prebuiltTemplates.map(pb => {
              const stepTypes = pb.steps?.map(s => s.action_type) || [];
              const emailCount = stepTypes.filter(t => t === 'send_email').length;
              const taskCount = stepTypes.filter(t => t === 'create_task').length;
              const notifCount = stepTypes.filter(t => t === 'notification').length;

              return (
                <Card
                  key={pb.key}
                  data-testid={`prebuilt-${pb.key}`}
                  className={`bg-white border-gray-200 transition-all ${pb.installed ? 'opacity-70' : 'hover:border-[#0073ea]/40 hover:shadow-md'}`}
                >
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-[#0073ea]" />
                          <h3 className="text-sm font-semibold text-gray-900">{pb.name}</h3>
                        </div>
                        <Badge variant="outline" className={`mt-1.5 ${typeColors[pb.campaign_type] || typeColors.General} text-xs`}>
                          {pb.campaign_type}
                        </Badge>
                      </div>
                    </div>

                    <p className="text-xs text-gray-500 leading-relaxed">{pb.description}</p>

                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">{pb.steps_count} steps</span>
                      {emailCount > 0 && <span className="flex items-center gap-1"><Mail className="h-3 w-3 text-blue-500" />{emailCount}</span>}
                      {taskCount > 0 && <span className="flex items-center gap-1"><ClipboardList className="h-3 w-3 text-amber-500" />{taskCount}</span>}
                      {notifCount > 0 && <span className="flex items-center gap-1"><Bell className="h-3 w-3 text-purple-500" />{notifCount}</span>}
                    </div>

                    {/* Step timeline preview */}
                    <div className="flex items-center gap-1 flex-wrap">
                      {pb.steps?.map((step, i) => {
                        const delayLabel = step.delay_type === 'immediate' ? 'Now'
                          : step.delay_value === 1 ? `${step.delay_type === 'hours' ? '1h' : '1d'}`
                          : `${step.delay_value}${step.delay_type === 'hours' ? 'h' : 'd'}`;
                        return (
                          <div key={i} className="flex items-center gap-1">
                            {i > 0 && <ChevronRight className="h-3 w-3 text-gray-200" />}
                            <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gray-50 text-gray-500 text-xs border border-gray-200">
                              {step.action_type === 'send_email' && <Mail className="h-3 w-3" />}
                              {step.action_type === 'create_task' && <ClipboardList className="h-3 w-3" />}
                              {step.action_type === 'notification' && <Bell className="h-3 w-3" />}
                              <Clock className="h-2.5 w-2.5 text-gray-400" />
                              <span>{delayLabel}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {pb.installed ? (
                      <Button
                        variant="outline" size="sm"
                        onClick={() => navigate(`/drip-campaigns/${pb.installed_id}/edit`)}
                        className="w-full border-gray-300 text-gray-600"
                        data-testid={`edit-installed-${pb.key}`}
                      >
                        Already Installed — Edit
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        data-testid={`install-prebuilt-${pb.key}`}
                        onClick={() => handleInstallPrebuilt(pb.key)}
                        disabled={installing === pb.key}
                        className="w-full bg-[#0073ea] hover:bg-[#0060c2] text-white"
                      >
                        {installing === pb.key ? (
                          'Installing...'
                        ) : (
                          <><Download className="h-3.5 w-3.5 mr-1.5" /> Install Campaign</>
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Campaign List */}
      {activeTab !== 'Templates' && (loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-24 rounded-xl bg-gray-200 animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Zap className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">{search ? 'No campaigns match your search' : 'No campaigns yet. Create your first drip campaign.'}</p>
          {!search && (
            <Button onClick={() => navigate('/drip-campaigns/new')} className="mt-4 bg-[#0073ea] hover:bg-[#0060c2] text-white">
              <Plus className="h-4 w-4 mr-2" /> Create Campaign
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(campaign => (
            <Card
              key={campaign.id}
              data-testid={`campaign-card-${campaign.id}`}
              className="bg-white border-gray-200 hover:border-[#0073ea]/30 hover:shadow-sm transition-all cursor-pointer"
              onClick={() => navigate(`/drip-campaigns/${campaign.id}/edit`)}
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  {/* Status dot */}
                  <div className={`h-3 w-3 rounded-full flex-shrink-0 ${statusColors[campaign.status]}`} />

                  {/* Main info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-base font-semibold text-gray-900 truncate">{campaign.name}</h3>
                      <Badge variant="outline" className={`${typeColors[campaign.campaign_type] || typeColors.General} text-xs`}>
                        {campaign.campaign_type}
                      </Badge>
                      <Badge variant="outline" className="text-xs text-gray-500 border-gray-200">
                        {statusLabels[campaign.status]}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>{campaign.steps?.length || 0} steps</span>
                      <span>{campaign.leads_count || 0} active leads</span>
                      {campaign.business_hours_only && <span>Business hours only</span>}
                      {campaign.stop_on_statuses?.length > 0 && (
                        <span>Stops on: {campaign.stop_on_statuses.join(', ')}</span>
                      )}
                    </div>
                    {/* Step preview */}
                    {campaign.steps?.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-2">
                        {campaign.steps.slice(0, 5).map((step, i) => (
                          <div key={step.id || i} className="flex items-center gap-1">
                            {i > 0 && <ChevronRight className="h-3 w-3 text-gray-300" />}
                            <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-gray-50 text-gray-600 text-xs">
                              <StepIcon type={step.action_type} />
                              <span className="hidden sm:inline">{formatDelay(step)}</span>
                            </div>
                          </div>
                        ))}
                        {campaign.steps.length > 5 && (
                          <span className="text-xs text-gray-400 ml-1">+{campaign.steps.length - 5} more</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    {campaign.status === 'draft' || campaign.status === 'paused' ? (
                      <Button
                        variant="ghost" size="sm"
                        data-testid={`activate-campaign-${campaign.id}`}
                        onClick={() => handleStatusChange(campaign, 'active')}
                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-8 px-2"
                      >
                        <Play className="h-3.5 w-3.5 mr-1" /> Activate
                      </Button>
                    ) : (
                      <Button
                        variant="ghost" size="sm"
                        data-testid={`pause-campaign-${campaign.id}`}
                        onClick={() => handleStatusChange(campaign, 'paused')}
                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-8 px-2"
                      >
                        <Pause className="h-3.5 w-3.5 mr-1" /> Pause
                      </Button>
                    )}
                    <div className="relative">
                      <Button
                        variant="ghost" size="sm"
                        data-testid={`campaign-actions-${campaign.id}`}
                        onClick={() => setActionMenuId(actionMenuId === campaign.id ? null : campaign.id)}
                        className="text-gray-400 hover:text-gray-600 h-8 w-8 p-0"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                      {actionMenuId === campaign.id && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setActionMenuId(null)} />
                          <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                            <button
                              data-testid={`duplicate-campaign-${campaign.id}`}
                              onClick={() => handleDuplicate(campaign.id)}
                              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Copy className="h-3.5 w-3.5" /> Duplicate
                            </button>
                            <button
                              data-testid={`delete-campaign-${campaign.id}`}
                              onClick={() => { setShowDeleteConfirm(campaign.id); setActionMenuId(null); }}
                              className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                            >
                              <Trash2 className="h-3.5 w-3.5" /> Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ))}

      {/* Delete Confirmation */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-sm" data-testid="delete-campaign-dialog">
          <DialogHeader>
            <DialogTitle>Delete Campaign</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">Are you sure? This will also remove all active assignments for this campaign. This action cannot be undone.</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setShowDeleteConfirm(null)} className="text-gray-500">Cancel</Button>
            <Button
              data-testid="confirm-delete-campaign"
              onClick={() => handleDelete(showDeleteConfirm)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
